import { useUser } from "@/api/hooks/user";
import { SingleDropdown, type SingleDropdownOption } from "@/components/molecules/SingleDropdown/SingleDropdown";
import { getFeatureValue } from "@/types/featureSet";
import { useMemo } from "react";

export type ModelDropdownOption = {
  id: string;
  title: string;
  disabled?: boolean;
};

export type ModelDropdownProps = {
  options: ModelDropdownOption[];
  value?: string;
  onChange: (modelId: string) => void;
  placeholder: string;
  disabled?: boolean;
  "data-testid"?: string;
};

export function ModelDropdown({
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  "data-testid": dataTestId,
}: ModelDropdownProps) {
  const { features } = useUser();
  const modelRestriction = getFeatureValue(features, "data_config.available_models");

  const filteredOptions = useMemo(() => {
    if (!Array.isArray(modelRestriction)) {
      return options;
    }

    const allowedModelIds = modelRestriction.filter(
      (candidate): candidate is string => typeof candidate === "string" && !!candidate,
    );
    if (allowedModelIds.length === 0) {
      return options;
    }

    const allowedModelIdSet = new Set(allowedModelIds);
    return options.filter((option) => allowedModelIdSet.has(option.id));
  }, [modelRestriction, options]);

  const normalizedOptions: SingleDropdownOption<string>[] = filteredOptions.map((option) => ({
    value: option.id,
    label: option.title,
    disabled: option.disabled,
  }));

  return (
    <SingleDropdown
      options={normalizedOptions}
      value={value}
      onChange={(selectedModel) => onChange(selectedModel)}
      placeholder={placeholder}
      disabled={disabled || normalizedOptions.length === 1}
      data-testid={dataTestId}
    />
  );
}
